<script lang="ts" setup>
import UserLogin from "~/features/auth/login/UserLogin.vue";
import PasswordResetBtn from "~/features/forgot-password/PasswordResetBtn.vue";
import Modal from "~/components/ui/modals/Modal.vue";
import useMainStore from "~/shared/store";
import AppInput from "~/components/ui/form/formElements/AppInput.vue";
import AppInputPassword from "~/components/ui/form/formElements/AppInputPassword.vue";
import AppLink from "~/components/ui/links/AppLink.vue";
import InfoIcon from '~/assets/icons/statuses/16/info-circle-filled-red.svg'
import ArrowIcon from '~/assets/icons/arrows/24/arrow-left-black.svg'
import getInitialData from "~/composables/initial-data";
import getHeaderPosition from "~/composables/get-header-position";
import type {IAuthResponse, ILogin, TAuthErrors} from "~/shared/interfaces/auth";
import type {IValidationData} from "~/shared/interfaces/validator";
import DeletedUserModal from "../deleted-user/DeletedUserModal.vue";

const mainStore = useMainStore()
const emailConfig = {
  inputConfig: {
    label: 'Email',
    id: 'email',
    interactiveLabel: true,
    isFocused: true
  },
  validatorConfig: {
    required: true,
    emailFormat: true,
  },
}

const fieldError = ref(false)
const emailError = ref('')
const passwordError = ref('')
const email = ref('')
const deletedUserModalMessage = ref('')
let topIndent: string

if (process.client) {
  const headerPosition = getHeaderPosition()
  topIndent = `${headerPosition ? headerPosition.bottom : 0}px`
}

const modalTitle = computed(() => {
  if (mainStore.authModalType === 'email') {
    return 'Вход в профиль'
  }
  if (mainStore.authModalType === 'passwordRecovery' || mainStore.authModalType === 'passwordRecoverySuccess') {
    return 'Восстановление пароля'
  }
})

const authForm = ref<ILogin>({
  password: '',
  email: ''
})

function handleInput(event: IValidationData) {
  emailError.value = ''
  passwordError.value = ''
  fieldError.value = false
  event.id === 'authPassword' ?
    authForm.value.password = event.value :
    authForm.value.email = event.value
}

async function handleAuthResponse(event: IAuthResponse) {
  if (!!event.errors?.find(el => el.message)) {
    fieldError.value = true
  } else {
    mainStore.setAuthModalState(false)
    useRouter().go(0)
  }
}

function handleAuthErrors(event: TAuthErrors[]) {
  if (event.includes('emailEmpty')) {
    emailError.value = 'Заполните поле'
  }

  if (event.includes('emailFormat')) {
    emailError.value = 'Почта введена неверно'
  }

  if (event.includes('emailNotExist')) {
    emailError.value = 'Аккаунта с этой почтой не существует'
  }

  if (event.includes('passwordEmpty')) {
    passwordError.value = 'Заполните поле'
  }

  if (event.includes('emailDeleted')) {
    mainStore.setAuthModalState(false)
    mainStore.setAuthModalType('email')
    mainStore.setDeletedUserModalState(true)
    deletedUserModalMessage.value = event[1]
  }
}

function closeModal() {
  mainStore.setAuthModalState(false)
  setTimeout(() => {
    mainStore.setAuthModalType('email')
  }, 300)
  emailError.value = ''
  passwordError.value = ''
  email.value = ''
  authForm.value.email = ''
  authForm.value.password = ''
}

function handlePasswordReset(event: string) {
  email.value = event
  mainStore.setAuthModalType('passwordRecoverySuccess')
}

watch(() => mainStore.isAuthModal, () => {
  if (mainStore.isAuthModal) {
    const headerPosition = getHeaderPosition()
    topIndent = `${headerPosition ? headerPosition.bottom : 0}px`
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    })
  }
})
</script>

<template>
  <Transition name="fade">
    <Modal
      :modal-title="modalTitle"
      :modal-is-open="mainStore.isAuthModal"
      @close-modal="closeModal"
      :full-height-on-mobile="true"
      v-if="mainStore.isAuthModal"
      :is-scrolling="true"
      class="auth-modal"
    >
      <form
        class="auth-form"
        v-if="mainStore.authModalType === 'email'"
        @submit.prevent
      >
        <div
          class="auth-form__hint"
          v-if="fieldError"
        >
          <div class="auth-form__hint-img">
            <InfoIcon/>
          </div>
          <div class="auth-form__hint-txt">
            Неверный e-mail или пароль
          </div>
        </div>
        <AppInput
          :input-config="emailConfig.inputConfig"
          @get-data="handleInput"
          :errorMessage="emailError"
        />
        <AppInputPassword
          id="authPassword"
          label="Пароль"
          :is-full-width="true"
          :is-hint="false"
          @pass-password="handleInput"
          :errorMessage="passwordError"
        />
        <AppLink
          text="Забыли пароль?"
          url="javascript:"
          @click="mainStore.setAuthModalType('passwordRecovery')"
          :font-weight="500"
        />
        <UserLogin
          class="auth-form__submit"
          :login-form="authForm"
          @pass-response="handleAuthResponse"
          @pass-errors="handleAuthErrors"
        />
      </form>
      <form
        class="auth-form auth-form_password-recovery"
        v-if="mainStore.authModalType === 'passwordRecovery'"
        @submit.prevent
      >
        <ArrowIcon
          class="auth-form__back"
          @click="mainStore.setAuthModalType('email')"
        />
        <p class="auth-form__txt">Введите почту, указанную при регистрации. Мы отправим на нее инструкцию по&nbsp;восстановлению пароля</p>
        <AppInput
          :input-config="emailConfig.inputConfig"
          @get-data="handleInput"
          :errorMessage="emailError"
        />
        <PasswordResetBtn
          class="auth-form__submit"
          :email="authForm.email"
          @pass-errors="handleAuthErrors"
          @pass-response="handlePasswordReset"
        />
      </form>
      <div
        class="password-reset"
        v-if="mainStore.authModalType === 'passwordRecoverySuccess'"
      >
        <p>Письмо для восстановления пароля отправлено на почту {{ email }}. Если письмо не приходит, проверьте папку
          спам</p>
      </div>
      <div
        class="auth-modal__bottom"
        v-if="mainStore.authModalType === 'email'"
      >
        <div class="auth-modal__bottom-link">
          <span>Нет аккаунта?</span>
          <AppLink
            text="Зарегистрироваться"
            url="/registration"
            @click="closeModal"
            :font-weight="500"
          />
        </div>
        <div class="auth-modal__bottom-policy">
          Авторизируясь на сайте, вы соглашаетесь с&nbsp;<AppLink type="legal" text="условиями пользования сайта" url="/terms" target="_blank"/>
          и
          <AppLink type="legal" text="политикой конфиденциальности" url="/privacy" target="_blank"/>
        </div>
      </div>
    </Modal>
  </Transition>
  <Teleport to="body">
    <ClientOnly>
      <DeletedUserModal
        v-if="mainStore.isDeletedUserModal"
        :text=deletedUserModalMessage
      />
    </ClientOnly>
  </Teleport>
</template>

<style lang="scss" scoped>
.auth-modal{
  @include xs-breakpoint {
    top: 0;
  }

  :deep(.modal-content) {
    border-radius: 20px;
    overflow: hidden;
    padding: 0;
    width: 416px;
    @include xs-breakpoint {
      border-radius: 0;
      top: 0 !important;
      width: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      // height: calc(100% - v-bind(topIndent)) !important;
    }
  }

  &.active{
    :deep(.modal-content.full-height) {
      bottom: auto !important;
    }
  }
  :deep(.modal-content__title) {
    @include font(24px, 28px, 600);
  }

  :deep(.modal-content__wrapper) {
    @include sm-breakpoint {
      height: calc(100% - 60px);
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
  }

  :deep(.modal-close__wrapper) {
    @include sm-breakpoint {
      width: 24px !important;
      height: 24px !important;
      top: 24px !important;
      right: 20px !important;
    }
  }

  :deep(.modal-content__title) {
    padding: 32px 32px 0;
    @include xs-breakpoint {
      padding: 68px 20px 0;
    }
  }

  &__bottom {
    margin-top: 40px;
    border-radius: 20px 20px 0 0;
    background-color: $bg_light-grey;
    padding: 24px 32px 32px;
    text-align: center;
    @include sm-breakpoint {
      margin-top: auto;
      padding: 24px 20px;
    }

    a {
      display: inline;
    }

    &-link {
      display: flex;
      gap: 8px;
      justify-content: center;
    }

    &-policy {
      margin-top: 16px;
      @include font(14px, 20px);
      color: $content_secondary;
    }
  }
}

.auth-form {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 32px;
  @include xs-breakpoint {
    padding: 0 20px;
  }

  &__back {
    display: none;
    @include xs-breakpoint {
      display: block;
      position: absolute;
      top: 24px;
      left: 20px;
    }
  }

  &_password-recovery {
    padding-bottom: 32px;
    margin-top: 12px;
    @include xs-breakpoint {
      padding: 0 20px;
    }
  }

  &__hint {
    @include font(14px, 20px);
    padding: 10px;
    background-color: $bg-danger;
    border-radius: 6px;
    display: flex;
    gap: 8px;
    align-items: center;

    &-img {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
  }

  &__submit {
    margin-top: 12px;
  }
}

.password-reset {
  padding: 12px 32px 32px;
  @include xs-breakpoint {
    padding: 12px 20px 0;
  }
}
</style>
